import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../firebase/firebase';

// ── Custom bike icon ──────────────────────────────────────────────────────────
const bikeIcon = new L.Icon({
  iconUrl: '/markers/bike.png',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -20],
});

const offlineBikeIcon = new L.Icon({
  iconUrl: '/markers/bike.png',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -16],
  className: 'opacity-50',
});

// ── Types ──────────────────────────────────────────────────────────────────────
interface RiderLive {
  docId: string;
  name?: string;
  phone?: string;
  vehicleType?: string;
  vehicleNumber?: string;
  active?: boolean;
  available?: boolean;
  geohash?: string;
  currentLocation?: {
    latitude?: number;
    longitude?: number;
    updatedAt?: any;
  };
  currentOrderId?: string;
  salary?: number;
}

// ── Recenter helper ───────────────────────────────────────────────────────────
function RecenterMap({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => { map.setView(center, 13); }, [center, map]);
  return null;
}

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  bg: '#f6f8fb',
  panel: '#0A1712',
  card: '#ffffff',
  primary: '#21808D',
  teal: '#4ECDC4',
  green: '#21C98E',
  muted: '#8BAAB8',
  danger: '#ef4444',
  border: 'rgba(33,128,141,0.18)',
  text: '#1a2332',
};

const statusColor = (r: RiderLive) => {
  if (r.currentOrderId) return '#f59e0b';
  if (r.active && r.available) return C.green;
  if (r.active) return C.primary;
  return '#64748b';
};

const statusLabel = (r: RiderLive) => {
  if (r.currentOrderId) return 'On delivery';
  if (r.active && r.available) return 'Online · Available';
  if (r.active) return 'Online · Busy';
  return 'Offline';
};

type FilterMode = 'all' | 'online' | 'available' | 'busy' | 'offline';

export default function LiveRiderMap() {
  const [riders, setRiders] = useState<RiderLive[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<RiderLive | null>(null);
  const [center, setCenter] = useState<[number, number]>([26.1433, 91.7362]); // Guwahati default
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'zapee_delivery_partners')),
      (snap) => {
        const list: RiderLive[] = snap.docs.map(d => ({ docId: d.id, ...d.data() } as RiderLive));
        setRiders(list);
        setLoading(false);
        setLastUpdate(new Date());

        // Auto-center on first valid location
        const first = list.find(r => r.currentLocation?.latitude && r.currentLocation?.longitude);
        if (first?.currentLocation?.latitude && first?.currentLocation?.longitude) {
          setCenter([first.currentLocation.latitude, first.currentLocation.longitude]);
        }
      },
      (err) => { console.error('LiveRiderMap:', err); setLoading(false); },
    );
    return () => unsub();
  }, []);

  const filtered = riders.filter(r => {
    const loc = r.currentLocation;
    const hasLoc = !!(loc?.latitude && loc?.longitude);
    if (!hasLoc) return false;

    const matchSearch = !search || [r.name, r.phone, r.vehicleNumber].some(v =>
      v?.toLowerCase().includes(search.toLowerCase()),
    );

    let matchFilter = true;
    if (filter === 'online') matchFilter = !!r.active;
    if (filter === 'available') matchFilter = !!r.active && !!r.available;
    if (filter === 'busy') matchFilter = !!r.currentOrderId;
    if (filter === 'offline') matchFilter = !r.active;

    return matchSearch && matchFilter;
  });

  const counts = {
    all: riders.filter(r => r.currentLocation?.latitude).length,
    online: riders.filter(r => r.active && r.currentLocation?.latitude).length,
    available: riders.filter(r => r.active && r.available && r.currentLocation?.latitude).length,
    busy: riders.filter(r => r.currentOrderId && r.currentLocation?.latitude).length,
    offline: riders.filter(r => !r.active && r.currentLocation?.latitude).length,
  };

  const focusRider = useCallback((r: RiderLive) => {
    setSelected(r);
    if (r.currentLocation?.latitude && r.currentLocation?.longitude) {
      setCenter([r.currentLocation.latitude, r.currentLocation.longitude]);
    }
  }, []);

  function fmtTime(ts: any): string {
    if (!ts) return '—';
    try {
      const d = ts?.toDate ? ts.toDate() : new Date(typeof ts === 'number' ? ts : String(ts));
      if (isNaN(d.getTime())) return '—';
      const diff = Math.floor((Date.now() - d.getTime()) / 60000);
      if (diff < 1) return 'Just now';
      if (diff < 60) return `${diff}m ago`;
      return `${Math.floor(diff / 60)}h ${diff % 60}m ago`;
    } catch { return '—'; }
  }

  return (
    <div style={ss.root}>
      {/* ── Left panel ── */}
      <div style={ss.leftPanel}>
        {/* Header */}
        <div style={ss.panelHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="material-icons-round" style={{ fontSize: 18, color: '#4ECDC4', lineHeight: 1 }}>map</span>
            <div>
              <div style={ss.panelTitle}>Live Rider Map</div>
              <div style={ss.panelSub}>
                {lastUpdate ? `Updated ${fmtTime(lastUpdate)}` : 'Connecting…'}
              </div>
            </div>
          </div>
          <div style={ss.liveBadge}>
            <div style={ss.liveDot} />
            LIVE
          </div>
        </div>

        {/* Search */}
        <div style={ss.searchWrap}>
          <span style={ss.searchIcon} className="material-icons-round">search</span>
          <input
            style={ss.searchInput}
            placeholder="Name, phone, vehicle…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Filter chips */}
        <div style={ss.chips}>
          {(['all', 'online', 'available', 'busy', 'offline'] as FilterMode[]).map(f => (
            <button
              key={f}
              style={{ ...ss.chip, ...(filter === f ? ss.chipActive : {}) }}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              <span style={{ ...ss.chipCount, ...(filter === f ? ss.chipCountActive : {}) }}>
                {counts[f]}
              </span>
            </button>
          ))}
        </div>

        {/* Stats row */}
        <div style={ss.statsRow}>
          {[
            { label: 'Total', value: riders.length, color: C.muted },
            { label: 'Online', value: counts.online, color: C.green },
            { label: 'Delivering', value: counts.busy, color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} style={ss.statBox}>
              <div style={{ ...ss.statNum, color: s.color }}>{s.value}</div>
              <div style={ss.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Rider list */}
        <div style={ss.riderList}>
          {loading && (
            <div style={ss.centerMsg}>Loading riders…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div style={ss.centerMsg}>No riders match filter</div>
          )}
          {filtered.map(r => (
            <div
              key={r.docId}
              style={{ ...ss.riderCard, ...(selected?.docId === r.docId ? ss.riderCardActive : {}) }}
              onClick={() => focusRider(r)}
            >
              <div style={{ ...ss.riderDot, backgroundColor: statusColor(r) }} />
              <div style={ss.riderInfo}>
                <div style={ss.riderName}>{r.name || 'Unknown'}</div>
                <div style={ss.riderMeta}>{r.phone || '—'} · {r.vehicleType || '—'}</div>
                <div style={{ ...ss.riderStatus, color: statusColor(r) }}>
                  {statusLabel(r)}
                </div>
              </div>
              <div style={ss.riderTime}>
                {fmtTime(r.currentLocation?.updatedAt)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Map ── */}
      <div style={ss.mapWrap}>
        {loading ? (
          <div style={ss.mapLoading}>
            <span className="material-icons-round" style={{ fontSize: 40, color: C.teal }}>map</span>
            <div style={{ color: C.muted, marginTop: 12 }}>Loading map…</div>
          </div>
        ) : (
          <MapContainer
            center={center}
            zoom={13}
            style={{ width: '100%', height: '100%' }}
            zoomControl
          >
            <RecenterMap center={center} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {filtered.map(r => {
              const lat = r.currentLocation?.latitude!;
              const lon = r.currentLocation?.longitude!;
              return (
                <Marker
                  key={r.docId}
                  position={[lat, lon]}
                  icon={r.active ? bikeIcon : offlineBikeIcon}
                  eventHandlers={{ click: () => setSelected(r) }}
                >
                  <Popup>
                    <div style={{ minWidth: 180, fontFamily: 'inherit' }}>
                      <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 6 }}>
                        {r.name || 'Unknown Rider'}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.7 }}>
                        <div>📞 {r.phone || '—'}</div>
                        <div>🛵 {r.vehicleType || '—'} · {r.vehicleNumber || '—'}</div>
                        <div>💰 ₹{r.salary || 0}/mo</div>
                        <div style={{ marginTop: 6, fontWeight: 700, color: statusColor(r) }}>
                          ● {statusLabel(r)}
                        </div>
                        <div style={{ marginTop: 4, color: '#94a3b8' }}>
                          Updated {fmtTime(r.currentLocation?.updatedAt)}
                        </div>
                        {r.geohash && (
                          <div style={{ marginTop: 2, color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>
                            {r.geohash}
                          </div>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}

        {/* Selected rider card overlay */}
        {selected && (
          <div style={ss.selectedCard}>
            <div style={ss.selectedHeader}>
              <div>
                <div style={ss.selectedName}>{selected.name || 'Unknown'}</div>
                <div style={{ ...ss.selectedStatus, color: statusColor(selected) }}>
                  ● {statusLabel(selected)}
                </div>
              </div>
              <button style={ss.selectedClose} onClick={() => setSelected(null)}>✕</button>
            </div>
            <div style={ss.selectedBody}>
              <div style={ss.selectedRow}><span>📞</span>{selected.phone || '—'}</div>
              <div style={ss.selectedRow}><span>🛵</span>{selected.vehicleType || '—'} · {selected.vehicleNumber || '—'}</div>
              <div style={ss.selectedRow}><span>💰</span>₹{selected.salary || 0}/month</div>
              {selected.currentOrderId && (
                <div style={ss.selectedRow}><span>📦</span>Order: {selected.currentOrderId}</div>
              )}
              {selected.currentLocation?.latitude && (
                <div style={ss.selectedRow}>
                  <span>📍</span>
                  {selected.currentLocation.latitude.toFixed(6)}, {selected.currentLocation.longitude!.toFixed(6)}
                </div>
              )}
              {selected.geohash && (
                <div style={ss.selectedRow}><span>🌐</span><code>{selected.geohash}</code></div>
              )}
              <div style={{ ...ss.selectedRow, color: '#94a3b8' }}>
                <span>🕐</span>Updated {fmtTime(selected.currentLocation?.updatedAt)}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .leaflet-container { z-index: 1; }
        .opacity-50 { opacity: 0.45; filter: grayscale(60%); }
      `}</style>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const ss: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: C.bg,
  },
  leftPanel: {
    width: 300,
    backgroundColor: C.panel,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRight: `1px solid ${C.border}`,
    flexShrink: 0,
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 16px 12px',
    borderBottom: `1px solid ${C.border}`,
  },
  panelTitle: { fontSize: 15, fontWeight: 800, color: '#fff' },
  panelSub: { fontSize: 11, color: C.muted, marginTop: 2 },
  liveBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(33,201,142,0.15)',
    border: '1px solid rgba(33,201,142,0.4)',
    borderRadius: 20,
    padding: '3px 10px',
    fontSize: 10,
    fontWeight: 800,
    color: C.green,
    letterSpacing: 1,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: C.green,
    animation: 'pulse 1.5s infinite',
  },
  searchWrap: {
    position: 'relative',
    padding: '12px 12px 8px',
  },
  searchIcon: {
    position: 'absolute',
    left: 22,
    top: '50%',
    transform: 'translateY(-30%)',
    fontSize: 18,
    color: C.muted,
  },
  searchInput: {
    width: '100%',
    height: 36,
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    paddingLeft: 34,
    paddingRight: 10,
    fontSize: 13,
    color: '#fff',
    boxSizing: 'border-box',
    outline: 'none',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 5,
    padding: '4px 12px 10px',
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    borderRadius: 20,
    border: `1px solid ${C.border}`,
    backgroundColor: 'transparent',
    color: C.muted,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  },
  chipActive: {
    backgroundColor: 'rgba(33,128,141,0.22)',
    borderColor: C.primary,
    color: C.teal,
  },
  chipCount: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: '0 5px',
    fontSize: 10,
    fontWeight: 700,
    color: C.muted,
  },
  chipCountActive: { color: C.teal, backgroundColor: 'rgba(33,128,141,0.2)' },
  statsRow: {
    display: 'flex',
    borderTop: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
  },
  statBox: {
    flex: 1,
    textAlign: 'center',
    padding: '8px 4px',
    borderRight: `1px solid ${C.border}`,
  },
  statNum: { fontSize: 18, fontWeight: 800 },
  statLabel: { fontSize: 10, color: C.muted, marginTop: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  riderList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 8px',
  },
  centerMsg: { textAlign: 'center', color: C.muted, fontSize: 13, padding: '40px 0' },
  riderCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 10,
    marginBottom: 4,
    cursor: 'pointer',
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid transparent',
    transition: 'all 0.15s',
  },
  riderCardActive: {
    backgroundColor: 'rgba(33,128,141,0.15)',
    border: `1px solid ${C.primary}`,
  },
  riderDot: { width: 9, height: 9, borderRadius: '50%', marginTop: 5, flexShrink: 0 },
  riderInfo: { flex: 1, minWidth: 0 },
  riderName: { fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  riderMeta: { fontSize: 11, color: C.muted, marginTop: 1 },
  riderStatus: { fontSize: 11, fontWeight: 600, marginTop: 2 },
  riderTime: { fontSize: 10, color: C.muted, flexShrink: 0, marginTop: 3 },
  mapWrap: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  mapLoading: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: C.bg,
  },
  selectedCard: {
    position: 'absolute',
    bottom: 20,
    left: 16,
    width: 300,
    backgroundColor: C.panel,
    borderRadius: 14,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
    zIndex: 1000,
    border: `1px solid ${C.border}`,
  },
  selectedHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px 10px',
    borderBottom: `1px solid ${C.border}`,
  },
  selectedName: { fontSize: 15, fontWeight: 800, color: '#fff' },
  selectedStatus: { fontSize: 12, fontWeight: 600, marginTop: 2 },
  selectedClose: {
    background: 'rgba(255,255,255,0.08)',
    border: 'none',
    color: C.muted,
    borderRadius: 6,
    width: 28,
    height: 28,
    cursor: 'pointer',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectedBody: { padding: '10px 16px 14px', display: 'flex', flexDirection: 'column', gap: 6 },
  selectedRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#8BAAB8' },
};
